import { zodResolver } from '@hookform/resolvers/zod';
import { api } from '@packages/backend/convex/_generated/api';
import {
  type AppUpdateJsPolicyCvx,
  type AppUpdateNativePolicyCvx,
  appUpdateJsPolicyStrictSchema,
  CLIENT_APP_NAMES,
  getUserErrorMessage,
  PLATFORM_APP_STORE_LISTINGS,
  type StorePlatform,
} from '@packages/shared';
import { createFileRoute } from '@tanstack/react-router';
import { useConvex } from 'convex/react';
import { useQuery } from 'convex-helpers/react';
import { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import { ActionButton } from '@/components/ActionButton';
import { ErrorAlert } from '@/components/ErrorAlert';
import { FormWrapper } from '@/components/FormWrapper';
import { LoadingIcon } from '@/components/LoadingIcon';
import { NumberInput } from '@/components/NumberInput';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription } from '@/components/ui/card';
import { FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { NativePolicyForm, NativePolicyFormHeader } from '@/features/app-updates/NativePolicyForm';
import { cn } from '@/lib/cn';
import { env } from '@/lib/env';
import { handleSubmitErrors } from '@/lib/handleSubmitErrors';

export const Route = createFileRoute('/a/app-updates')({
  staticData: {
    breadcrumbs: [{ label: 'App updates' }],
  },
  component: AppUpdatesPage,
});

/**
 * Per app: its store listings, then its JS updates, one row per native version plus one for its web
 * build (the web apps have only that one). Latest offers an update; a forced update blocks the app
 * until it has updated. consumer-native has JS updates only: its store listings are per brand, on
 * each venue's Mobile App Config page. The store listings are this stage's
 * (`PLATFORM_APP_STORE_LISTINGS`): the builds that use its backend.
 */
function AppUpdatesPage() {
  const { data, isPending, error, isError } = useQuery(api.appUpdates.getAll);

  if (isError) return <ErrorAlert message={error.message} title="Error loading app updates" />;
  if (isPending) return <LoadingIcon />;

  return (
    <div className="space-y-8">
      {CLIENT_APP_NAMES.map((app) => {
        const listings = PLATFORM_APP_STORE_LISTINGS[env.VITE_STAGE].filter(
          (listing) => listing.app === app,
        );
        const { forcedNativeVersion, releasedNativeVersion } = getStoreNativeVersions(
          listings,
          data.nativePolicies,
        );
        // The web row first, then native versions, newest first
        const jsPolicies = data.jsPolicies
          .filter((jsPolicy) => jsPolicy.app === app)
          .sort(
            (a, b) =>
              (b.nativeVersion ?? Number.POSITIVE_INFINITY) -
              (a.nativeVersion ?? Number.POSITIVE_INFINITY),
          );
        return (
          <div className="w-full max-w-4xl space-y-3" key={app}>
            <h2 className="text-xl font-semibold">{app}</h2>
            <Card>
              <CardContent className="space-y-8">
                {listings.length > 0 && (
                  <section className="space-y-2">
                    <h3 className="font-semibold">Store builds</h3>
                    <div className="overflow-x-auto">
                      <NativePolicyFormHeader />
                      {listings.map(({ bundleId, platform }) => (
                        <NativePolicyForm
                          key={`${bundleId}-${platform}`}
                          bundleId={bundleId}
                          platform={platform}
                          nativePolicies={data.nativePolicies}
                        />
                      ))}
                    </div>
                  </section>
                )}

                <section className="space-y-2">
                  <h3 className="font-semibold">JS updates</h3>
                  {jsPolicies.length === 0 ? (
                    <CardDescription>No JS updates deployed yet.</CardDescription>
                  ) : (
                    <div className="overflow-x-auto">
                      <div className={cn(JS_GRID_CLASS_NAME, 'pb-1 text-xs text-muted-foreground')}>
                        <span>Build</span>
                        <span>Latest JS</span>
                        <span>Force update below</span>
                        <span>Status</span>
                      </div>
                      {jsPolicies.map((jsPolicy) => (
                        <JsPolicyForm
                          key={jsPolicy._id}
                          jsPolicy={jsPolicy}
                          storeForcedNativeVersion={forcedNativeVersion}
                          storeReleasedNativeVersion={releasedNativeVersion}
                        />
                      ))}
                    </div>
                  )}
                </section>
              </CardContent>
            </Card>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Where an app's store listings on this page stand, for its JS rows:
 * - `forcedNativeVersion`: the lowest native version its listings force the store update to.
 *   `null` when the app has no listing here (consumer-native), or one listing forces no update.
 * - `releasedNativeVersion`: the native version in the App Store, from the iOS listing (Google Play
 *   has no lookup). `null` with no iOS listing here, or before Apple's lookup has found it.
 */
const getStoreNativeVersions = (
  listings: { bundleId: string; platform: StorePlatform }[],
  nativePolicies: AppUpdateNativePolicyCvx[],
) => {
  const policies = listings.map(({ bundleId, platform }) =>
    nativePolicies.find((row) => row.bundleId === bundleId && row.platform === platform),
  );
  const forcedVersions = policies.map((policy) => policy?.minNativeVersion ?? null);
  const appStoreVersion =
    policies.find((policy) => policy?.platform === 'ios')?.appleAppStoreInfo?.appStoreVersion ??
    null;

  return {
    forcedNativeVersion:
      policies.length > 0 && forcedVersions.every((version) => version !== null)
        ? Math.min(...forcedVersions)
        : null,
    releasedNativeVersion: appStoreVersion === null ? null : Number(appStoreVersion.split('.')[0]),
  };
};

/**
 * Columns of the JS updates heading row and each `JsPolicyForm` row. Wider than a narrow card, so
 * their container scrolls sideways (`overflow-x-auto`).
 */
const JS_GRID_CLASS_NAME =
  'grid grid-cols-[4.5rem_4.5rem_7rem_minmax(8rem,1fr)_8rem] items-center gap-2 px-2';

const minJsVersionFormSchema = appUpdateJsPolicyStrictSchema.pick({ minJsVersion: true });

type JsPolicyFormProps = {
  jsPolicy: AppUpdateJsPolicyCvx;
  /** Below it, every store listing forces the store update, so this row's JS policy never applies. */
  storeForcedNativeVersion: number | null;
  /** Above it, this native version isn't in the App Store yet: only test builds get its JS. */
  storeReleasedNativeVersion: number | null;
};

/**
 * One native version's row, or the web build's: sets the JS version below which it is blocked.
 * Empty until one is saved. A native version's row is muted while a forced store update overrides
 * it (and locked) or while it isn't in the App Store yet.
 */
const JsPolicyForm = ({
  jsPolicy,
  storeForcedNativeVersion,
  storeReleasedNativeVersion,
}: JsPolicyFormProps) => {
  const convex = useConvex();
  const { app, nativeVersion, latestJsVersion, minJsVersion } = jsPolicy;

  const form = useForm({
    resolver: zodResolver(minJsVersionFormSchema),
    defaultValues: { minJsVersion },
  });
  const formMinJsVersion = useWatch({ control: form.control, name: 'minJsVersion' });

  // Ensure un-saved user changes are kept on Convex sync
  useEffect(() => {
    form.reset({ minJsVersion }, { keepDirty: true, keepDirtyValues: true });
  }, [minJsVersion, form]);

  const saveMinJsVersion = async (nextMinJsVersion: number | null) => {
    try {
      await convex.mutation(api.appUpdates.setMinJsVersion, {
        jsPolicy: { app, nativeVersion, minJsVersion: nextMinJsVersion },
      });
      // The saved value is the new baseline, so nothing is dirty any more
      form.reset({ minJsVersion: nextMinJsVersion });
      toast.success(nextMinJsVersion === null ? 'Unblocked' : 'Saved');
    } catch (error) {
      toast.error("Couldn't save the minimum JS version", {
        description: getUserErrorMessage(error),
      });
    }
  };

  const isOverridden =
    nativeVersion !== null &&
    storeForcedNativeVersion !== null &&
    nativeVersion < storeForcedNativeVersion;
  const isUnreleased =
    nativeVersion !== null &&
    storeReleasedNativeVersion !== null &&
    nativeVersion > storeReleasedNativeVersion;
  const isInFlight =
    minJsVersion !== null && (latestJsVersion === null || minJsVersion > latestJsVersion);

  return (
    <FormWrapper {...form}>
      <form
        onSubmit={form.handleSubmit(
          ({ minJsVersion: nextMinJsVersion }) => saveMinJsVersion(nextMinJsVersion),
          handleSubmitErrors,
        )}
        className={cn(
          JS_GRID_CLASS_NAME,
          'border-t py-2.5 text-sm',
          (isOverridden || isUnreleased) && 'bg-muted text-muted-foreground',
        )}
      >
        <span className="font-medium">{nativeVersion ?? 'Web'}</span>
        <span>{latestJsVersion === null ? 'None' : `#${latestJsVersion}`}</span>
        <FormField
          control={form.control}
          name="minJsVersion"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <NumberInput
                  {...field}
                  min={1}
                  value={field.value}
                  disabled={isOverridden}
                  aria-label={`Force update below for ${app} ${nativeVersion === null ? 'web' : `native ${nativeVersion}`}`}
                  className="w-20"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {isOverridden ? (
          <Badge variant="outline" className="text-muted-foreground">
            Store update to v{storeForcedNativeVersion} forced
          </Badge>
        ) : isUnreleased ? (
          <Badge variant="outline" className="text-muted-foreground">
            Not in the App Store yet
          </Badge>
        ) : minJsVersion === null ? (
          <Badge variant="secondary" className="text-muted-foreground">
            Not forced
          </Badge>
        ) : isInFlight ? (
          <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300">
            Waiting for #{minJsVersion} to deploy
          </Badge>
        ) : (
          <Badge className="bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300">
            Below #{minJsVersion} blocked
          </Badge>
        )}
        <div className="flex justify-end gap-2">
          <ActionButton
            disabled={
              isOverridden || form.formState.isSubmitting || formMinJsVersion === minJsVersion
            }
            loading={form.formState.isSubmitting}
            type="submit"
            size="sm"
          >
            Save
          </ActionButton>
          {minJsVersion !== null && (
            <ActionButton
              type="button"
              variant="outline"
              size="sm"
              disabled={isOverridden}
              onClick={() => saveMinJsVersion(null)}
            >
              Clear
            </ActionButton>
          )}
        </div>
      </form>
    </FormWrapper>
  );
};
